/**
 * Public client-facing Adherence Rewards form (/patient).
 *
 * Flow:
 *   1. Landing form — DOB, name, milestone, email → submit → returns a
 *      reference code the client should save.
 *   2. Success screen — displays the code + summary and lets them jump
 *      straight into Manage.
 *   3. Manage screen — client re-enters their code, sees the current
 *      status, and can edit or delete while still `pending`. Once the
 *      admin marks the request `approved`, the manage view locks and
 *      just displays the payment date / method.
 *
 * No auth — the alphanumeric code IS the auth. Amount is always
 * computed server-side from the milestone key so clients can't tamper.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREEN = "#0F3B2E";
const GREEN_DARK = "#0A2A21";
const LABEL = "#2D5A4C";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const BG = "#F4F3EF";
const RED = "#B4351B";
const BLUE = "#4A6FB0";
const SUCCESS = "#2E7D5B";

const PHONE_TEL = "1-844-925-2873";
const PHONE_DISPLAY = "1-844-925-CURE (2873)";

// ── helpers ────────────────────────────────────────────────────────────

function todayLong() {
  return new Date().toLocaleDateString("en-CA", {
    year: "numeric", month: "short", day: "numeric",
  });
}

function fmtDateLong(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return "—";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", {
    year: "numeric", month: "short", day: "numeric",
  });
}

function normalizeCode(v) {
  return (v || "").replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 8);
}

function displayCode(v) {
  const r = normalizeCode(v);
  return r.length === 8 ? `${r.slice(0, 4)}-${r.slice(4)}` : r;
}

// ── shared UI atoms ────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: "100vh", background: BG, color: "#1C2B26",
    fontFamily: "'Work Sans', system-ui, sans-serif",
    paddingTop: 72,  // clear the fixed site Navbar (h-16-ish) so our green hero isn't hidden underneath
  },
  hero: {
    background: GREEN, color: "#fff", padding: "22px 20px 26px",
  },
  heroInner: { maxWidth: 640, margin: "0 auto", textAlign: "center" },
  heroTitle: { margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: "-.01em" },
  heroSub: { fontSize: 12, opacity: .78, marginTop: 2, letterSpacing: ".02em" },

  container: { maxWidth: 640, margin: "0 auto", padding: "0 20px 96px" },
  card: {
    background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14,
    padding: 20, marginTop: 20,
  },
  label: {
    display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
    textTransform: "uppercase", color: LABEL, margin: "14px 0 4px",
    paddingLeft: 12,
  },
  input: {
    width: "100%", height: 42, padding: "0 12px", fontSize: 14,
    color: "#1C2B26", border: `1px solid ${BORDER}`, borderRadius: 10,
    background: "#fff", outline: "none", fontFamily: "inherit",
    boxSizing: "border-box",
  },
  row: { display: "flex", gap: 8 },
  disc: {
    background: "#FEF9EA", border: "1px solid #EED8A2", borderRadius: 10,
    padding: "12px 14px", fontSize: 12, color: "#7A5A00",
    lineHeight: 1.55, marginTop: 14,
  },
  btnPrimary: {
    height: 46, padding: "0 20px", borderRadius: 10,
    background: GREEN, color: "#fff", border: 0,
    fontWeight: 800, fontSize: 14, cursor: "pointer",
    fontFamily: "inherit", width: "100%",
  },
  btnGhost: {
    height: 42, padding: "0 20px", borderRadius: 10,
    background: "#fff", color: GREEN, border: `1.5px solid ${GREEN}`,
    fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit",
  },
  btnDanger: {
    height: 46, padding: "0 20px", borderRadius: 10,
    background: "#fff", color: RED, border: `1.5px solid ${RED}`,
    fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit",
  },
};

// ── milestone radio list ───────────────────────────────────────────────

// Display grouping for the /patient milestone list. Keys map to the
// server-side catalog in backend/adherence.py (amounts/labels come from
// the API; only the grouping + explanatory copy lives here).
const MILESTONE_GROUPS = [
  {
    id: "milestones",
    title: "Individual milestones",
    footnote: "Only submit a request for a milestone you have completed. Unqualified requests will be denied.",
    keys: ["doctor_appt", "month_1", "month_2", "month_3", "blood_work_eot"],
  },
  {
    id: "full",
    title: "Full program — $400",
    keys: ["full_treatment"],
    footnote: "The $400 covers it all: your doctor appointment, all three months of treatment, and the post-treatment bloodwork. Only choose this option if you opted in from the beginning to receive one $400 payment after completing the entire program.",
  },
  {
    id: "other",
    title: "Other items",
    keys: ["blood_work", "outreach", "friend_referral"],
  },
];

// Per-item disclaimers shown under specific milestones in the list.
const ITEM_NOTES = {
  blood_work: "Only clients pre-qualified by phone for bloodwork qualify for this payment.",
  outreach: "Only outreach work approved by the program qualifies for this payment.",
  friend_referral: "Only referred friends who test HCV positive qualify for this payment.",
};

function MilestoneList({ items, value, onChange, disabled }) {
  const groupedKeys = new Set(MILESTONE_GROUPS.flatMap((g) => g.keys));
  const groups = MILESTONE_GROUPS
    .map((g) => ({ ...g, items: g.keys.map((k) => items.find((m) => m.key === k)).filter(Boolean) }))
    .filter((g) => g.items.length > 0);
  // Any future milestone the API adds without a mapping still shows up.
  const ungrouped = items.filter((m) => !groupedKeys.has(m.key));
  if (ungrouped.length) groups.push({ id: "more", title: "More", items: ungrouped });

  const row = (m, isLast) => {
    const active = value === m.key;
    return (
      <div key={m.key}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(m.key)}
          data-testid={`adh-milestone-${m.key}`}
          style={{
            display: "flex", width: "100%", padding: "14px", border: 0,
            background: active ? "rgba(15,59,46,0.05)" : "#fff",
            borderBottom: isLast ? "none" : `1px solid ${BORDER}`,
            cursor: disabled ? "not-allowed" : "pointer",
            fontFamily: "inherit", fontSize: 14, textAlign: "left",
            alignItems: "center", justifyContent: "space-between",
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <span style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1, minWidth: 0, paddingRight: 10 }}>
            <span style={{
              width: 20, height: 20, borderRadius: "50%",
              border: `1.5px solid ${active ? GREEN : "#B4BDB8"}`,
              background: active ? GREEN : "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, marginTop: ITEM_NOTES[m.key] ? 1 : 0,
            }}>
              {active && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />}
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block" }}>{m.label}</span>
              {ITEM_NOTES[m.key] && (
                <span style={{ display: "block", fontSize: 11, color: MUTED, fontWeight: 400, lineHeight: 1.45, marginTop: 3 }}>
                  {ITEM_NOTES[m.key]}
                </span>
              )}
            </span>
          </span>
          <span style={{ fontWeight: 700, color: GREEN, flexShrink: 0 }}>${m.amount}</span>
        </button>
      </div>
    );
  };

  return (
    <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
      {groups.map((g, gi) => (
        <div key={g.id} data-testid={`adh-milestone-group-${g.id}`}>
          <div style={{
            background: "#F4F3EF", padding: "8px 14px",
            borderTop: gi === 0 ? "none" : `1px solid ${BORDER}`,
            borderBottom: `1px solid ${BORDER}`,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 800, letterSpacing: ".12em",
              textTransform: "uppercase", color: LABEL,
            }}>{g.title}</div>
            {g.note && <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2, lineHeight: 1.4 }}>{g.note}</div>}
          </div>
          {g.items.map((m, i) => row(m, i === g.items.length - 1 && !g.footnote))}
          {g.footnote && (
            <div style={{
              fontSize: 11.5, color: MUTED, lineHeight: 1.5,
              padding: "8px 14px 12px", background: "#FDFCFA",
              borderTop: `1px dashed ${BORDER}`,
            }}>{g.footnote}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Friend referral extra input + HCV note ─────────────────────────────

function FriendReferralBlock({ show, value, onChange, disabled }) {
  if (!show) return null;
  return (
    <>
      <div style={{
        background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 10,
        padding: "14px 16px", marginTop: 10,
      }}>
        <label style={{ ...S.label, marginTop: 0, paddingLeft: 2 }}>
          Referred friend&apos;s full name <span style={{ color: RED }}>*</span>
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="e.g. Bob Anderson"
          data-testid="adh-referred-person"
          style={{
            ...S.input, height: 42, background: "#F9F8F6", borderRadius: 10,
          }}
        />
      </div>
    </>
  );
}

// ── Referral spotlight — ALWAYS visible promo, rendered below the form ──

function ReferralSpotlight() {
  const GREEN_2 = "#2E7D5B";
  const LEAF = "#B7DEC7";
  return (
      <div
        data-testid="adh-friend-referral-spotlight"
        style={{
          position: "relative", overflow: "hidden", marginTop: 12,
          borderRadius: 18, border: `1px solid ${LEAF}`,
          background: `
            radial-gradient(320px 200px at 100% -20%, rgba(46,125,91,.18), transparent 70%),
            radial-gradient(300px 200px at -10% 130%, rgba(15,59,46,.10), transparent 70%),
            linear-gradient(180deg,#F5FBF7 0%, #FFFDF9 100%)
          `,
        }}
      >
        {/* Header strip: icon left, centred text */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "14px 16px 12px",
          borderBottom: "1px dashed rgba(15,59,46,.15)",
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12,
            background: GREEN, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, fontWeight: 900, flexShrink: 0,
            boxShadow: "0 4px 12px -3px rgba(15,59,46,.35)",
          }} aria-hidden>🤝</div>
          <div style={{ flex: 1, minWidth: 0, textAlign: "center", paddingRight: 32 }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: ".22em",
              textTransform: "uppercase", color: GREEN_2, marginBottom: 4,
              whiteSpace: "nowrap",
            }}>Friend referral program</div>
            <div style={{
              fontSize: 16, fontWeight: 800, color: GREEN,
              lineHeight: 1.25, letterSpacing: "-.01em",
            }}>Help a friend get treated</div>
          </div>
        </div>

        {/* Money hero */}
        <div style={{ textAlign: "center", padding: "18px 16px 22px" }}>
          <div style={{
            fontSize: 11, fontWeight: 800, letterSpacing: ".22em",
            textTransform: "uppercase", color: GREEN_2, marginBottom: 6,
          }}>You earn</div>
          <div style={{
            fontSize: 64, lineHeight: 1, fontWeight: 900, color: GREEN,
            letterSpacing: "-.045em", marginBottom: 8,
            background: `linear-gradient(180deg,${GREEN} 0%, ${GREEN_2} 100%)`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>$200</div>
          <div style={{
            display: "inline-block", fontSize: 12, fontWeight: 700,
            color: MUTED, letterSpacing: ".14em", textTransform: "uppercase",
            padding: "4px 10px", borderRadius: 999,
            background: "rgba(15,59,46,.06)",
          }}>per referral</div>
        </div>

        {/* Qualifier note */}
        <div style={{
          margin: "0 14px 14px", padding: "14px 16px",
          background: "rgba(255,255,255,.7)",
          border: "1px solid rgba(15,59,46,.12)",
          borderRadius: 12,
          display: "flex", gap: 12, alignItems: "center",
          fontSize: 12.5, color: "#365548", lineHeight: 1.6, textAlign: "left",
        }}>
          <div style={{
            flexShrink: 0, width: 26, height: 26, borderRadius: "50%",
            background: GREEN, color: "#fff", fontSize: 13, fontWeight: 900,
            display: "flex", alignItems: "center", justifyContent: "center",
          }} aria-hidden>✓</div>
          <div style={{ flex: 1 }}>
            The friend you register must test <b style={{ color: GREEN }}>positive for HCV</b> to
            qualify for the $200 bonus. No amount is paid if the result is negative.
          </div>
        </div>

        {/* CTA */}
        <div style={{ padding: "0 14px 16px" }}>
          <div style={{
            fontSize: 11.5, color: MUTED, textAlign: "center",
            marginBottom: 8, lineHeight: 1.45,
          }}>Call HepCURE to refer your friend for treatment</div>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="adh-friend-referral-call"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 10, width: "100%", padding: "14px 16px",
              background: GREEN, color: "#fff", textDecoration: "none",
              fontWeight: 800, fontSize: 15, letterSpacing: ".01em",
              borderRadius: 12,
              boxShadow: "0 6px 16px -6px rgba(15,59,46,.55)",
              whiteSpace: "nowrap",
            }}
          >
            <span>Call {PHONE_DISPLAY}</span>
          </a>
        </div>
      </div>
  );
}

// ── Submit form ────────────────────────────────────────────────────────

function SubmitForm({ milestones, onSubmitted, onGoManage }) {
  const [dob, setDob] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [milestone, setMilestone] = useState("");
  const [referredPerson, setReferredPerson] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const selected = useMemo(
    () => milestones.find((m) => m.key === milestone),
    [milestones, milestone],
  );

  const canSubmit =
    firstName.trim() && lastName.trim() && dob && email &&
    (milestone !== "friend_referral" || referredPerson.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/adherence/requests`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        dob,
        email: email.trim(),
        milestone,
        referred_person: milestone === "friend_referral" ? referredPerson.trim() : null,
      });
      onSubmitted(data, { firstName, lastName, email });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to submit — please try again.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={S.hero}>
        <div style={S.heroInner}>
          <h1 style={S.heroTitle}>HepCURE Adherence</h1>
          <div style={S.heroSub}>Rewards for sharing your treatment milestones</div>
        </div>
      </div>

      <div style={S.container}>
        <div style={S.card}>
          <div style={{ fontSize: 13, color: MUTED, marginBottom: 6 }}>
            Complete the form below to receive your payment.
          </div>

          <form onSubmit={submit}>
            <div style={S.row}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Date</label>
                <input
                  type="text"
                  value={todayLong()}
                  readOnly
                  style={{ ...S.input, background: BG }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Date of birth *</label>
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  data-testid="adh-dob"
                  required
                  style={S.input}
                />
              </div>
            </div>
            <div style={{
              fontSize: 11, color: MUTED, marginTop: 6, lineHeight: 1.5,
              textAlign: "center", maxWidth: 280, marginLeft: "auto", marginRight: "auto",
            }}>
              Your name + DOB are used to confirm your<br />identity against your patient record.
            </div>

            <div style={S.row}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>First name *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="adh-first-name"
                  required
                  placeholder="Jane"
                  style={S.input}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Last name *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  data-testid="adh-last-name"
                  required
                  placeholder="Doe"
                  style={S.input}
                />
              </div>
            </div>

            <label style={{ ...S.label, whiteSpace: "nowrap", fontSize: 10.5, letterSpacing: ".1em" }}>Which milestone have you completed? *</label>
            <div style={{ fontSize: 11, color: MUTED, padding: "0 12px 6px", lineHeight: 1.4 }}>
              Submit an individual request for each item completed.
            </div>
            <MilestoneList items={milestones} value={milestone} onChange={setMilestone} />

            <FriendReferralBlock
              show={milestone === "friend_referral"}
              value={referredPerson}
              onChange={setReferredPerson}
            />

            <div style={{
              background: GREEN, color: "#fff", padding: "12px 14px", borderRadius: 10,
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginTop: 14, fontWeight: 700,
            }}>
              <span>Total to receive</span>
              <span style={{ fontSize: 18 }}>${selected?.amount ?? 0}</span>
            </div>

            <label style={S.label}>Email for e-transfer *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="adh-email"
              required
              placeholder="jane.doe@example.com"
              style={S.input}
            />

            <div style={S.disc}>
              <b style={{ color: "#5E4400" }}>⚠️ Please read before submitting</b>
              <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                <li style={{ marginBottom: 6 }}>
                  If auto-deposit is <b>not</b> set up, the security answer / password is: <b>hepcure</b>
                </li>
                <li style={{ marginBottom: 6 }}>
                  All payment requests submitted by <b>12 a.m. on Tuesdays and Saturdays</b> will be processed by <b>4 p.m.</b>
                </li>
                <li style={{ marginBottom: 6 }}>
                  Double-check your email as we cannot cancel or intercept once submitted.
                </li>
                <li style={{ marginBottom: 6 }}>
                  You&apos;ll get a reference code. Use it to <b>edit or delete</b> your request BEFORE it is processed.
                  Once processed the request cannot be undone and HepCURE is not responsible for any errors after that point.
                </li>
                <li>
                  <b>Data use:</b> By submitting, you consent to HepCURE collecting your treatment milestone data
                  and using it in a <b>de-identified</b> manner for research, reporting and program improvement.
                  Your payment is in recognition of sharing this information. Personal identifiers are never shared publicly.
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={!canSubmit || saving}
              data-testid="adh-submit"
              style={{
                ...S.btnPrimary,
                marginTop: 14,
                opacity: (!canSubmit || saving) ? 0.5 : 1,
                cursor: (!canSubmit || saving) ? "not-allowed" : "pointer",
              }}
            >{saving ? "Submitting…" : "Submit request"}</button>

            <div style={{ textAlign: "center", marginTop: 12, fontSize: 13, color: MUTED }}>
              Already submitted?{" "}
              <button
                type="button"
                onClick={onGoManage}
                data-testid="adh-open-manage"
                style={{
                  background: "transparent", border: 0, color: BLUE, fontWeight: 600,
                  cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 13,
                }}
              >Check / manage status →</button>
            </div>
          </form>

          {/* Referral promo — always visible, even when another milestone is picked */}
          <ReferralSpotlight />
        </div>
      </div>
    </>
  );
}

// ── Success screen with the code ───────────────────────────────────────

function SuccessScreen({ result, submitted, onManage }) {
  return (
    <>
      <div style={S.hero}>
        <div style={S.heroInner}>
          <h1 style={S.heroTitle}>HepCURE Adherence</h1>
          <div style={S.heroSub}>Request received</div>
        </div>
      </div>
      <div style={S.container}>
        <div style={{ ...S.card, textAlign: "center", paddingTop: 30 }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%", background: SUCCESS,
            color: "#fff", fontSize: 30, fontWeight: 800,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            marginBottom: 12,
          }}>✓</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: GREEN }}>Request received</div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 6, maxWidth: 320, margin: "6px auto 20px" }}>
            Please save this reference code — you&apos;ll need it to check or edit your request.
          </div>
          <div style={{
            background: GREEN, color: "#fff", borderRadius: 14, padding: "22px 20px",
            margin: "0 auto 18px", maxWidth: 360,
          }}>
            <div style={{ fontSize: 10, letterSpacing: ".24em", textTransform: "uppercase", opacity: .72 }}>
              Reference code
            </div>
            <div style={{
              fontFamily: "'JetBrains Mono','Menlo',monospace",
              fontSize: 28, letterSpacing: ".24em", fontWeight: 800, margin: "8px 0 14px",
            }} data-testid="adh-code-display">{result.code}</div>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(result.code);
                toast.success("Code copied");
              }}
              style={{
                background: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.32)",
                color: "#fff", padding: "6px 14px", borderRadius: 6, fontSize: 12,
                fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}
            >📋 Copy code</button>
          </div>

          <div style={{
            background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12,
            padding: 16, textAlign: "left", fontSize: 13, maxWidth: 360, margin: "0 auto",
          }}>
            <Row k="Amount" v={<b style={{ color: GREEN }}>${result.amount}</b>} />
            <Row k="Milestone" v={<b>{result.milestone_label}</b>} />
            <Row k="E-transfer to" v={<b>{submitted?.email}</b>} />
            <Row k="Status" v={<StatusPill status="pending" />} last />
          </div>

          <div style={{ fontSize: 12, color: MUTED, marginTop: 16, maxWidth: 320, margin: "16px auto 0" }}>
            All payment requests submitted by <b>12 a.m. on Tuesdays and Saturdays</b> will be processed by <b>4 p.m.</b> Save this code —
            it&apos;s the only way to edit or delete your request before it&apos;s processed.
          </div>

          <button
            onClick={onManage}
            style={{ ...S.btnPrimary, marginTop: 20 }}
            data-testid="adh-goto-manage"
          >Check status / manage</button>
        </div>
      </div>
    </>
  );
}

function Row({ k, v, last }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      marginBottom: last ? 0 : 10,
    }}>
      <span style={{ color: MUTED }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

function StatusPill({ status }) {
  const isPending = status === "pending";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "4px 10px",
      borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: ".04em",
      background: isPending ? "rgba(229,138,46,0.10)" : "rgba(46,125,91,0.14)",
      color: isPending ? "#E58A2E" : SUCCESS,
      border: `1px solid ${isPending ? "rgba(229,138,46,0.35)" : "rgba(46,125,91,0.35)"}`,
    }} data-testid="adh-status-pill">
      {isPending ? "Pending" : "Approved"}
    </span>
  );
}

// ── Manage screen (code lookup + edit/delete) ──────────────────────────

function ManageScreen({ milestones, prefillCode, onBack }) {
  const [codeInput, setCodeInput] = useState(prefillCode || "");
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Editable fields (pending only)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [milestone, setMilestone] = useState("");
  const [referredPerson, setReferredPerson] = useState("");

  const hydrate = (r) => {
    setRecord(r);
    setFirstName(r.first_name || "");
    setLastName(r.last_name || "");
    setDob(r.dob || "");
    setEmail(r.email || "");
    setMilestone(r.milestone || "");
    setReferredPerson(r.referred_person || "");
  };

  const lookup = async (silent = false) => {
    const raw = normalizeCode(codeInput);
    if (raw.length !== 8) {
      if (!silent) toast.error("Enter your 8-character code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/adherence/requests/by-code/${raw}`);
      hydrate(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "No request found for that code.";
      if (!silent) toast.error(msg);
      setRecord(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup if prefilled from success screen
  useEffect(() => {
    if (prefillCode) lookup(true);
  }, []);

  // When the status view renders (record just loaded), snap to the top so
  // users see the header — mirrors the form → success transition scroll.
  useEffect(() => {
    if (!record) return;
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [record?.code]);

  const save = async () => {
    if (!record || saving) return;
    if (milestone === "friend_referral" && !referredPerson.trim()) {
      toast.error("Referred friend&apos;s name is required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/adherence/requests/by-code/${record.code}`,
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          dob,
          email: email.trim(),
          milestone,
          referred_person: milestone === "friend_referral" ? referredPerson.trim() : null,
        },
      );
      hydrate(data);
      toast.success("Changes saved.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to save changes.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!record) return;
    try {
      await axios.delete(`${API}/adherence/requests/by-code/${record.code}`);
      toast.success("Request deleted.");
      setRecord(null);
      setCodeInput("");
      setConfirmDelete(false);
      // Route back to the initial submit-a-new-request screen instead of the
      // code lookup screen.
      onBack?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to delete.";
      toast.error(msg);
    }
  };

  const selectedAmount = useMemo(
    () => milestones.find((m) => m.key === milestone)?.amount || 0,
    [milestones, milestone],
  );
  const isPending = record?.status === "pending";
  const isApproved = record?.status === "approved";

  return (
    <>
      <div style={S.hero}>
        <div style={S.heroInner}>
          <h1 style={S.heroTitle}>
            {record ? `Ref #${displayCode(record.code)}` : "Manage your request"}
          </h1>
          <div style={S.heroSub}>
            {record ? (isPending ? "Pending" : "Payment issued") : "Enter your code to view or edit"}
          </div>
        </div>
      </div>

      <div style={S.container}>
        {!record && (
          <div style={S.card}>
            <label style={S.label}>Reference code *</label>
            <input
              type="text"
              value={displayCode(codeInput)}
              onChange={(e) => setCodeInput(normalizeCode(e.target.value))}
              placeholder="XXXX-XXXX"
              data-testid="adh-code-input"
              style={{
                ...S.input, fontFamily: "'JetBrains Mono','Menlo',monospace",
                letterSpacing: ".2em", fontSize: 16, textTransform: "uppercase",
              }}
            />
            <button
              onClick={() => lookup(false)}
              disabled={loading}
              data-testid="adh-lookup"
              style={{ ...S.btnPrimary, marginTop: 14, opacity: loading ? 0.6 : 1 }}
            >{loading ? "Looking up…" : "Check status"}</button>

            <button
              type="button"
              onClick={onBack}
              style={{
                background: "transparent", border: 0, color: BLUE, fontWeight: 600,
                cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 13,
                marginTop: 14, display: "block", width: "100%", textAlign: "center",
              }}
            >← Back to submit a new request</button>
          </div>
        )}

        {record && (
          <div style={S.card}>
            {isApproved && (
              <div style={{
                background: BG, border: `1px solid ${BORDER}`, borderRadius: 10,
                padding: "10px 14px", marginBottom: 14, fontSize: 12,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span>Submitted <b>{fmtDateLong(record.date)}</b></span>
                <StatusPill status={record.status} />
              </div>
            )}

            {isApproved && (
              <div style={{
                background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12,
                padding: 16, fontSize: 13,
              }}>
                <Row k="Amount" v={<b style={{ color: GREEN }}>${record.amount}</b>} />
                <Row k="Payment date" v={<b>{fmtDateLong(record.payment_date)}</b>} />
                <Row k="Method" v={<b>{(record.payment_method || "eft").toUpperCase() === "EFT" ? "EFT / e-transfer" : "Cash"}</b>} />
                <Row k="Sent to" v={<b>{record.email}</b>} />
                <Row k="Milestone" v={<b>{record.milestone_label}</b>} />
                {record.referred_person && (
                  <Row k="Referred friend" v={<b>{record.referred_person}</b>} last />
                )}
              </div>
            )}

            {isApproved && (
              <div style={{
                background: BG, border: `1px solid ${BORDER}`, borderRadius: 10,
                padding: "12px 14px", marginTop: 14, fontSize: 12, color: MUTED,
              }}>
                This request has been processed and can no longer be modified.
                HepCURE is not responsible for any errors after processing.
              </div>
            )}

            {isPending && (
              <>
                <div style={S.row}>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Date</label>
                    <input
                      type="text"
                      value={fmtDateLong(record.date)}
                      readOnly
                      style={{ ...S.input, background: BG }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Date of birth *</label>
                    <input
                      type="date"
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                      style={S.input}
                    />
                  </div>
                </div>
                <div style={{
                  fontSize: 11, color: MUTED, marginTop: 6, lineHeight: 1.5,
                  textAlign: "center", maxWidth: 280, marginLeft: "auto", marginRight: "auto",
                }}>
                  Your name + DOB are used to confirm your<br />identity against your patient record.
                </div>

                <div style={S.row}>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>First name *</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      style={S.input}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Last name *</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      style={S.input}
                    />
                  </div>
                </div>

                <label style={S.label}>Milestone</label>
                <div style={{ fontSize: 11, color: MUTED, padding: "0 12px 6px", lineHeight: 1.4 }}>
                  Select one milestone per submission.
                </div>
                <MilestoneList items={milestones} value={milestone} onChange={setMilestone} />

                <FriendReferralBlock
                  show={milestone === "friend_referral"}
                  value={referredPerson}
                  onChange={setReferredPerson}
                />

                <label style={S.label}>Email for e-transfer *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={S.input}
                />

                <div style={{
                  background: GREEN, color: "#fff", padding: "12px 14px", borderRadius: 10,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  marginTop: 14, fontWeight: 700,
                }}>
                  <span>Updated total</span>
                  <span style={{ fontSize: 18 }}>${selectedAmount}</span>
                </div>

                <div style={S.disc}>
                  <b style={{ color: "#5E4400" }}>⚠️ Before saving changes</b>
                  <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                    <li style={{ marginBottom: 6 }}>
                      Edits and deletes are only possible while your request is <b>pending</b>.
                    </li>
                    <li>
                      Once we process the payment, the request <b>cannot be undone</b> and
                      HepCURE is not responsible for any errors after that point.
                    </li>
                  </ul>
                </div>

                {confirmDelete ? (
                  <div style={{
                    background: "#FEF0EE", border: `1px solid ${RED}`, borderRadius: 10,
                    padding: 14, marginTop: 14,
                  }}>
                    <div style={{ fontSize: 13, color: RED, fontWeight: 700, marginBottom: 10 }}>
                      <style>{`
                        .adh-del-line { display: block; }
                        @media (min-width: 640px) {
                          .adh-del-line { display: inline; }
                          .adh-del-line + .adh-del-line::before { content: " "; }
                        }
                      `}</style>
                      <span className="adh-del-line">Delete this request?</span>
                      <span className="adh-del-line">This cannot be undone.</span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(false)}
                        style={{ ...S.btnGhost, flex: 1 }}
                      >Cancel</button>
                      <button
                        type="button"
                        onClick={doDelete}
                        data-testid="adh-delete-confirm"
                        style={{ ...S.btnDanger, flex: 1, background: RED, color: "#fff", border: 0 }}
                      >Yes, delete</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      data-testid="adh-delete"
                      style={S.btnDanger}
                    >Delete request</button>
                    <button
                      type="button"
                      onClick={save}
                      disabled={saving}
                      data-testid="adh-save"
                      style={{ ...S.btnPrimary, opacity: saving ? 0.6 : 1 }}
                    >{saving ? "Saving…" : "Save changes"}</button>
                  </div>
                )}
              </>
            )}

            <button
              type="button"
              onClick={() => { setRecord(null); setCodeInput(""); }}
              style={{
                background: "transparent", border: 0, color: BLUE, fontWeight: 600,
                cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 13,
                marginTop: 14, display: "block", width: "100%", textAlign: "center",
              }}
            >← Enter a different code</button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Root page ──────────────────────────────────────────────────────────

export default function PatientAdherence() {
  const [milestones, setMilestones] = useState([]);
  const [screen, setScreen] = useState("form"); // form | success | manage
  const [result, setResult] = useState(null);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/adherence/milestones`).then((r) => {
      if (alive) setMilestones(r.data || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Whenever the visible screen changes, jump back to the top of the page.
  // A React state-swap keeps the same URL so browsers preserve scrollTop —
  // we force it manually. Uses raw scrollTop on both html + body to cover
  // Safari + iOS, and skips smooth so it happens before the user notices.
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [screen]);

  return (
    <div style={S.page} data-testid="adh-page">
      {screen === "form" && (
        <SubmitForm
          milestones={milestones}
          onSubmitted={(data, ctx) => {
            setResult(data);
            setSubmitted(ctx);
            setScreen("success");
          }}
          onGoManage={() => setScreen("manage")}
        />
      )}
      {screen === "success" && (
        <SuccessScreen
          result={result}
          submitted={submitted}
          onManage={() => setScreen("manage")}
        />
      )}
      {screen === "manage" && (
        <ManageScreen
          milestones={milestones}
          prefillCode={result?.code}
          onBack={() => { setScreen("form"); setResult(null); }}
        />
      )}
    </div>
  );
}
